import { STEP_TESTS_DIR } from "../stepConfig";

/** `executions.purpose` value for a step-test run (vs. a normal `"step"` run). */
export const STEP_TEST_PURPOSE = "step_test";

/** File the runner reads to learn the step's original entry command. */
export const STEP_COMMAND_FILE = "__stepCommand";

/** The runner prints one line `<sentinel> <json>` of normalized per-spec results. */
export const STEP_TEST_RESULTS_SENTINEL = "__STEP_TEST_RESULTS__";

export const STEP_TEST_DIFF_BEGIN = "__STEP_TEST_DIFF_BEGIN__";
export const STEP_TEST_DIFF_EXPECTED = "__STEP_TEST_DIFF_EXPECTED__";
export const STEP_TEST_DIFF_ACTUAL = "__STEP_TEST_DIFF_ACTUAL__";
export const STEP_TEST_DIFF_END = "__STEP_TEST_DIFF_END__";

export const STEP_TEST_CASES_DIR = `${STEP_TESTS_DIR}/step-tests`;

/** Step-authored volume serializer; its presence switches pin capture to serialized snapshots. */
export const STEP_TEST_SERIALIZER_FILE = `${STEP_TESTS_DIR}/serialize`;

/** Step-authored request-match-key serializer; its presence switches replay matching to keyed lookup and starts the normalize helper. */
export const STEP_TEST_REQUEST_SERIALIZER_FILE = `${STEP_TESTS_DIR}/serialize-request`;

/** Step-authored stdout serializer; replay pipes both expected and actual output through it before comparing. */
export const STEP_TEST_OUTPUT_SERIALIZER_FILE = `${STEP_TESTS_DIR}/serialize-output`;

/** Max size of a pinned step test's input or output (256 KB). */
export const MAX_STEP_TEST_BYTES = 262_144;

/** Sidecar holding the frozen instant a legacy test was captured under; the runner hints at a serializer when such a test fails, and re-pinning or unpinning deletes it. */
export const STEP_TEST_CLOCK_SUFFIX = ".clock";

/** File the capture script checks for the request fixtures to register for replay under the reserved capture set name. */
export const STEP_TEST_REQUESTS_FILE = "__stepTestRequests";

/** Reserved fixture-set name the capture run's requests are registered and replayed under. */
export const CAPTURE_FIXTURE_SET_NAME = "capture";

/** Prefix the capture script puts on its failure message when recorded requests diverge at capture time. */
export const STEP_TEST_REQUESTS_DIVERGED_PREFIX = "requests diverged: ";

export function isStepTestInputRelPath(relPath: string): boolean {
  return (
    relPath.startsWith(`${STEP_TEST_CASES_DIR}/`) && relPath.endsWith(".input")
  );
}

export function isStepTestMetaRelPath(relPath: string): boolean {
  return (
    relPath.startsWith(`${STEP_TEST_CASES_DIR}/`) && relPath.endsWith(".meta")
  );
}

export function stepTestNameFromRelPath(relPath: string): string {
  return relPath
    .slice(STEP_TEST_CASES_DIR.length + 1)
    .replace(/\.(input|output)$/, "");
}

export function stepTestNameFromMetaRelPath(relPath: string): string {
  return relPath.slice(STEP_TEST_CASES_DIR.length + 1, -".meta".length);
}

export function isValidStepTestName(name: string): boolean {
  return /^[A-Za-z0-9_-]{1,64}$/.test(name);
}

/** Sorted names of a step's pinned step tests, from its step-relative files. */
export function stepTestNamesFromFiles(
  files: readonly { path: string }[]
): string[] {
  return files
    .map((f) => f.path)
    .filter(isStepTestInputRelPath)
    .map(stepTestNameFromRelPath)
    .toSorted();
}

/** Sorted names of a step's pinned step tests, from a commit's full file paths. */
export function stepTestNamesFromCommitFiles(
  files: readonly { path: string }[],
  stepDir: string
): string[] {
  const prefix = `${stepDir}/`;
  return stepTestNamesFromFiles(
    files
      .filter((f) => f.path.startsWith(prefix))
      .map((f) => ({ path: f.path.slice(prefix.length) }))
  );
}

/** The next free `step-test-N` name given the already-taken names. */
export function nextStepTestName(taken: Iterable<string>): string {
  const set = new Set(taken);
  let n = 1;
  while (set.has(`step-test-${n}`)) n++;
  return `step-test-${n}`;
}

export function parseStepTestDiffs(
  stdout: string
): Map<string, { expected: string; actual: string }> {
  const result = new Map<string, { expected: string; actual: string }>();
  const lines = stdout.split("\n");
  let i = 0;
  while (i < lines.length) {
    if (!lines[i]!.startsWith(`${STEP_TEST_DIFF_BEGIN} `)) {
      i++;
      continue;
    }
    const name = lines[i]!.slice(STEP_TEST_DIFF_BEGIN.length + 1);
    i++;
    if (lines[i] !== STEP_TEST_DIFF_EXPECTED) continue;
    i++;
    const expected: string[] = [];
    while (i < lines.length && lines[i] !== STEP_TEST_DIFF_ACTUAL) {
      expected.push(lines[i]!);
      i++;
    }
    if (i >= lines.length) break;
    i++;
    const actual: string[] = [];
    while (i < lines.length && lines[i] !== STEP_TEST_DIFF_END) {
      actual.push(lines[i]!);
      i++;
    }
    result.set(name, {
      expected: expected.join("\n"),
      actual: actual.join("\n"),
    });
    i++;
  }
  return result;
}
